import React from 'react';
import { Sparkles, Stethoscope, Briefcase, Dumbbell, GraduationCap, Wrench, HardHat, Home, SprayCan } from 'lucide-react';
import { ImageName } from '../shared/media';

// SVG-Komponente für Treppenhausreinigung
export const FloorIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="#0034C4" style={style}>
    <path d="M120-80v-80h140v-180h180v-180h180v-180h180v-140h80v220H700v180H520v180H340v180H120Z" />
  </svg>
);

export type Paket = {
  name: string;
  for: string;
  recommended: boolean;
  features: string[];
};

/** Ein konkretes Problem der Zielgruppe samt Aufloesung. */
export type PainPoint = {
  /** Der Schmerz in den Worten des Kunden. */
  pain: string;
  /** Was ihn dieses Problem konkret kostet: Zeit, Geld, Risiko, Nerven. */
  consequence: string;
  /** Wie wir genau das loesen. */
  solution: string;
  /** Woran der Kunde die Loesung festmachen kann. Optional. */
  proof?: string;
};

/**
 * Eine Frage aus Kundensicht mit direkter Antwort. Wird sichtbar auf der Seite
 * und als FAQPage-Schema ausgegeben, beides aus derselben Quelle.
 * Nur reiner Text: das Schema traegt `answer` unveraendert als `text`.
 * Antworten duerfen keine Zusagen enthalten, die nicht schon auf der Seite
 * stehen; Paketleistungen werden ausdruecklich der Stufe zugeordnet.
 */
export type FaqItem = {
  question: string;
  answer: string;
};

/**
 * Ortsbezug einer Leistung fuer die lokale Suche. Nur belegbare Fakten:
 * keine Zusagen zu Anfahrts- oder Reaktionszeiten.
 */
export type LocalFocus = {
  headline: string;
  paragraphs: string[];
  orteTitle: string;
  /** Namentlich genannte Einsatzorte. Muessen in `standorte.ts` stehen, das prueft `services.test.ts`. */
  orte: string[];
};

export type Service = {
  slug: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  title: string;
  shortDesc: string;
  items: string[];
  /** Basisname des Fotos in assets/images (ohne Endung), siehe src/shared/media.ts. */
  image: ImageName;
  showcaseImage?: ImageName;
  price?: string;
  heroTitle: string;
  heroText: string;
  intro: string;
  benefits: string[];
  checklist?: string[];
  pakete?: Paket[];
  paketeTitle?: string;
  paketeIntro?: string;
  paketeHint?: string;
  /** Ueberschrift ueber den Schmerzpunkten. */
  painHeadline?: string;
  /** Probleme der Zielgruppe, jeweils mit Aufloesung. Ohne Angabe entfaellt die Sektion. */
  pains?: PainPoint[];
  /** Ueberschrift ueber den FAQ. */
  faqHeadline?: string;
  /** Haeufige Fragen vor dem Kontaktblock. Ohne Angabe entfallen Sektion und FAQPage-Schema. */
  faqs?: FaqItem[];
  /** Ortsbezogene Sektion vor dem Kontaktblock. Ohne Angabe entfaellt sie. */
  local?: LocalFocus;
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
};

const praxisPakete: Paket[] = [
  {
    name: 'Praxis Reinigung',
    for: 'Für kleine Praxen mit einfachem Reinigungsbedarf.',
    recommended: false,
    features: [
      'Regelmäßige Unterhaltsreinigung nach Praxisschluss',
      'Empfang, Wartebereich, Sanitär, Böden und Nebenräume',
      'Feste Reinigungstage',
      'Fester Ansprechpartner',
      'Vertretungsregelung bei Ausfall',
    ],
  },
  {
    name: 'Praxis Plus',
    for: 'Empfohlen für Arzt-, Zahnarzt- und Therapiepraxen.',
    recommended: true,
    features: [
      'Alle Leistungen aus Praxis Reinigung',
      'Individueller Reinigungs- und Desinfektionsplan',
      'Speziell eingewiesenes, geschultes Personal',
      'Getrennte, farbcodierte Reinigungsmaterialien',
      'Diskreter Umgang mit sensiblen Praxisbereichen',
      'Checkliste und Reinigungsprotokoll auf Wunsch für Ihre QM-Dokumentation',
    ],
  },
  {
    name: 'Praxis Komfort',
    for: 'Für Praxen, die möglichst wenig Aufwand mit der Reinigung haben möchten.',
    recommended: false,
    features: [
      'Alle Leistungen aus Praxis Plus',
      'Benannte Stammkraft mit fest eingewiesener Vertretung',
      'Regelmäßige Qualitätskontrolle',
      'Feedback nach der Startphase',
      'Nachbesserung bei berechtigten Beanstandungen',
      'Objektmappe vor Ort auf Wunsch',
    ],
  },
];

const praxisPains: PainPoint[] = [
  {
    pain: 'Die Reinigungskraft fällt aus — und Sie erfahren es morgens um sieben.',
    consequence:
      'Die Praxis öffnet trotzdem. Wartezimmer und Sanitärbereiche bleiben, wie sie sind, und Sie organisieren zwischen zwei Patienten Ersatz.',
    solution:
      'Wir arbeiten mit einem festen Team statt mit Subunternehmern. Fällt jemand aus, greift unsere Vertretungsregelung — ohne dass Sie etwas veranlassen müssen. Im Paket Praxis Komfort ist die Vertretung Ihrer Stammkraft fest bei Ihnen eingewiesen.',
    proof: 'Vertretungsregelung in allen drei Paketen',
  },
  {
    pain: 'Jede Woche steht jemand anderes abends allein in Ihren Behandlungsräumen.',
    consequence:
      'Die Einweisung beginnt jedes Mal von vorn. Und ein wirklich gutes Gefühl stellt sich bei dem, was in einer Praxis herumliegt, nie ein.',
    solution:
      'Bei uns reinigt ausschließlich unser eigenes, festes Team — keine Subunternehmer. Behandlungs-, Sanitär- und Wartebereiche werden strikt getrennt gereinigt, ab Praxis Plus mit speziell eingewiesenem Personal und farbcodierten Materialien. Im Paket Praxis Komfort ist eine benannte Stammkraft fest für Ihre Praxis zuständig.',
    proof: 'Festes Team in allen Paketen, benannte Stammkraft im Paket Praxis Komfort',
  },
  {
    pain: 'Vor der nächsten Begehung fehlt der Nachweis, was wann gereinigt wurde.',
    consequence:
      'Sie rekonstruieren aus dem Gedächtnis, was eigentlich dokumentiert sein müsste — und tragen das Risiko, wenn die Erinnerung nicht reicht.',
    solution:
      'Ab dem Paket Praxis Plus führen wir auf Wunsch Reinigungsprotokolle, die sich direkt in Ihre QM-Dokumentation einordnen lassen. Gereinigt und desinfiziert wird nach Ihrem Hygieneplan, nicht nach unserem Standard.',
    proof: 'Reinigungsprotokolle auf Wunsch ab Paket Praxis Plus',
  },
  {
    pain: 'Sie müssen hinterherlaufen, damit die Qualität nicht nachlässt.',
    consequence:
      'Aus der Leistung, die Ihnen Arbeit abnehmen sollte, wird eine weitere Aufgabe auf Ihrem Schreibtisch.',
    solution:
      'Im Paket Praxis Komfort kontrollieren wir selbst: regelmäßige Qualitätskontrollen, Feedback nach der Startphase und Nachbesserung bei berechtigten Beanstandungen.',
    proof: 'Qualitätskontrolle, Feedback und Nachbesserung im Paket Praxis Komfort',
  },
];

const praxisFaqs: FaqItem[] = [
  {
    question: 'Was kostet eine Praxisreinigung in Bielefeld und Gütersloh?',
    answer:
      'Unsere Preise beginnen bei 33,50 € pro Stunde netto zzgl. MwSt. Wie viele Stunden eine Reinigung braucht, hängt vor allem von Praxisgröße, Reinigungsintervall und Leistungsumfang ab. Als unverbindliche Schätzung: Eine Praxis mit 120 m², die zweimal pro Woche gereinigt wird, liegt nach unserem Preisrechner bei rund 390 € netto im Monat. Ihren verbindlichen Festpreis erhalten Sie nach einer kostenlosen Besichtigung.',
  },
  {
    question: 'Wann wird die Praxis gereinigt?',
    answer:
      'Außerhalb Ihrer Sprechzeiten, nach Praxisschluss. Die Reinigungstage legen wir gemeinsam fest; danach bleiben sie fest im Plan, sodass Ihr Team weiß, wann gereinigt wird.',
  },
  {
    question: 'Nach welchen Hygienevorgaben reinigen Sie?',
    answer:
      'Maßgeblich ist der Hygieneplan Ihrer Praxis; fachlich orientieren wir uns an den Empfehlungen der Kommission für Krankenhaushygiene und Infektionsprävention (KRINKO) beim Robert Koch-Institut. Kontaktflächen desinfizieren wir nach den Vorgaben Ihres Hygieneplans, Sanitärbereiche mit VAH-gelisteten Flächendesinfektionsmitteln, und Behandlungs-, Sanitär- und Wartezonen werden getrennt gereinigt. Ab dem Paket Praxis Plus erstellen wir zusätzlich einen individuellen Reinigungs- und Desinfektionsplan und arbeiten mit farbcodierten Materialien je Bereich.',
  },
  {
    question: 'Bereiten Sie auch Medizinprodukte wie Instrumente auf?',
    answer:
      'Nein. Die Aufbereitung von Medizinprodukten gehört nicht zu unserem Leistungsumfang und bleibt in der Verantwortung Ihrer Praxis. Wir übernehmen die Reinigung der Räume sowie die Flächendesinfektion nach Ihrem Hygieneplan.',
  },
  {
    question: 'Bekomme ich Nachweise für meine QM-Dokumentation oder die nächste Praxisbegehung?',
    answer:
      'Ab dem Paket Praxis Plus ja: Auf Wunsch führen wir Checklisten und Reinigungsprotokolle, die sich in Ihre QM-Dokumentation einordnen lassen. So können Sie bei einer Begehung belegen, was wann gereinigt wurde.',
  },
  {
    question: 'Was passiert, wenn die Reinigungskraft ausfällt?',
    answer:
      'Dann greift unsere Vertretungsregelung, ohne dass Sie etwas organisieren müssen. Sie ist in allen drei Paketen enthalten. Im Paket Praxis Komfort ist zusätzlich eine benannte Stammkraft für Ihre Praxis zuständig, deren Vertretung fest bei Ihnen eingewiesen ist.',
  },
  {
    question: 'Reinigen bei uns wechselnde Kräfte oder ein festes Team?',
    answer:
      'Ein festes Team. Wir sind ein Familienbetrieb, bei Ihnen reinigt ausschließlich unser eigenes, festes Team, keine Subunternehmer. Eine benannte Stammkraft, die fest für Ihre Praxis zuständig ist, gibt es im Paket Praxis Komfort.',
  },
  {
    question: 'Wie stellen Sie Verschwiegenheit in der Praxis sicher?',
    answer:
      'Wir unterschreiben Ihnen eine Verschwiegenheitserklärung. Was unsere Mitarbeiter bei der Reinigung in Ihrer Praxis sehen, etwa Patientenunterlagen, bleibt vertraulich.',
  },
  {
    question: 'Reinigen Sie auch Zahnarztpraxen?',
    answer:
      'Ja. Wir reinigen Zahnarztpraxen ebenso wie Arzt- und Therapiepraxen, nach Praxisschluss und nach dem Hygieneplan Ihrer Praxis. Alle drei Pakete stehen Ihnen offen.',
  },
  {
    question: 'Für welche Praxen und Orte sind Sie tätig?',
    answer:
      'Für Arzt-, Zahnarzt- und Therapiepraxen sowie weitere medizinische Einrichtungen in Bielefeld, Gütersloh und Umgebung, unter anderem in Rheda-Wiedenbrück, Verl, Halle (Westf.), Steinhagen und Herford. Alle Einsatzorte finden Sie auf unserer Seite Einsatzgebiete.',
  },
  {
    question: 'Wie läuft der Start der Zusammenarbeit ab?',
    answer:
      'Sie fragen eine kostenlose Besichtigung an, und wir melden uns innerhalb von 24 Stunden zurück. Vor Ort sehen wir uns Räume, Hygieneplan und Abläufe an. Danach erhalten Sie ein Festpreisangebot passend zu Praxisgröße, Intervall und Leistungsumfang. Im Paket Praxis Komfort gehört ein Feedback nach der Startphase dazu.',
  },
  {
    question: 'Wie lange läuft der Vertrag, und wie kann ich kündigen?',
    answer:
      'Der Vertrag läuft ein Jahr und verlängert sich danach automatisch um jeweils ein weiteres Jahr. Kündigen können Sie mit einer Frist von drei Monaten vor Ablauf der Laufzeit.',
  },
  {
    question: 'Sind Sie versichert?',
    answer: 'Ja. Wir haben eine Betriebshaftpflichtversicherung mit 3 Mio. € Deckungssumme.',
  },
];

const treppenhausPains: PainPoint[] = [
  {
    pain: 'Die Beschwerden über das Treppenhaus landen auf Ihrem Tisch, nicht beim Dienstleister.',
    consequence:
      'Sie klären Dinge, die Sie nie gesehen haben — und müssen dem Eigentümer trotzdem begründen, wofür die Reinigung bezahlt wird.',
    solution:
      'Wir reinigen in festen Intervallen, wöchentlich oder 14-tägig, systematisch von oben nach unten. Auffälligkeiten im Haus melden wir Ihnen, bevor ein Mieter sie meldet.',
    proof: 'Meldung von Auffälligkeiten inklusive',
  },
  {
    pain: 'Die Rechnung lässt sich nicht sauber auf die Nebenkosten umlegen.',
    consequence:
      'Sie rechnen von Hand um, und bei der nächsten Abrechnungsprüfung steht die Position zur Diskussion.',
    solution:
      'Wir bereiten die Abrechnung so auf, dass sie sich direkt auf die Nebenkosten umlegen lässt — ohne dass Sie etwas umrechnen müssen.',
    proof: 'Abrechnung für die Nebenkostenumlage aufbereitet',
  },
  {
    pain: 'Bei Urlaub oder Krankheit fällt die Reinigung schlicht aus.',
    consequence:
      'Zwei Wochen ungeputztes Treppenhaus fallen in jedem Haus auf — und der Anruf kommt wieder bei Ihnen an.',
    solution:
      'Für Ihr Objekt ist ein fester Mitarbeiter zuständig, mit organisierter Vertretung bei Urlaub und Krankheit. Der Rhythmus bleibt, auch wenn einmal jemand anderes kommt.',
    proof: 'Fester Mitarbeiter mit organisierter Vertretung',
  },
  {
    pain: 'Sie erklären jedem neuen Dienstleister, wie eine Hausverwaltung arbeitet.',
    consequence:
      'Schlüsselübergabe, Zuständigkeiten, Abrechnungsrhythmus, Ansprechpartner im Haus — alles von vorn, bei jedem einzelnen Objekt.',
    solution:
      'Wir arbeiten regelmäßig mit Hausverwaltungen und kennen die Abläufe. Zusätzliche Bereiche wie Kellergänge, Fahrrad- und Müllräume nehmen wir auf Wunsch in denselben Auftrag.',
    proof: 'Erfahrung mit Hausverwaltungen',
  },
];

// Zielgruppe Buero: der Entscheider sitzt selbst im Objekt. Sein Schmerz ist
// die Stoerung des Betriebs und die schleichend nachlassende Qualitaet.
const bueroPains: PainPoint[] = [
  {
    pain: 'Der Staubsauger läuft, während Ihr Team telefoniert.',
    consequence:
      'Die Konzentration ist weg, das Kundengespräch gestört — und abends steht die eigentliche Reinigung trotzdem noch aus.',
    solution:
      'Wir reinigen zu Ihren Wunschzeiten: vor Arbeitsbeginn, nach Feierabend oder am Wochenende. Das Zeitfenster bestimmen Sie, nicht wir.',
    proof: 'Flexible Reinigungszeiten',
  },
  {
    pain: 'Am Anfang war es gründlich. Nach drei Monaten wird sichtbar weniger gemacht.',
    consequence:
      'Sie merken es zuerst an Kleinigkeiten und müssen dann die unangenehme Diskussion führen, für die Sie eigentlich keine Zeit haben.',
    solution:
      'Wir arbeiten nach dokumentierten Checklisten je Raum und kontrollieren regelmäßig selbst nach. Was vereinbart wurde, bleibt nachvollziehbar.',
    proof: 'Dokumentierte Checklisten und regelmäßige Qualitätskontrollen',
  },
  {
    pain: 'Ständig neue Gesichter — und alle haben einen Schlüssel zu Ihren Räumen.',
    consequence:
      'Jede neue Person muss eingewiesen werden. Und bei Unterlagen, die abends auf den Schreibtischen liegen, ist das mehr als eine organisatorische Frage.',
    solution:
      'Bei uns kommt ein festes, eingewiesenes Team statt wechselnder Subunternehmer — dieselben Personen, die Ihre Räume und Ihre Regeln kennen.',
    proof: 'Festes, eingewiesenes Reinigungsteam',
  },
  {
    pain: 'Eine Erkältung geht durchs Büro und legt nacheinander das halbe Team lahm.',
    consequence:
      'Türklinken, Lichtschalter und Telefone werden bei der normalen Reinigung meist nur abgewischt, nicht desinfiziert.',
    solution:
      'Auf Wunsch desinfizieren wir Kontaktflächen gezielt — Türklinken, Lichtschalter, Telefone und Besprechungsräume — zusätzlich zur Unterhaltsreinigung.',
    proof: 'Desinfektionsreinigung von Kontaktflächen auf Wunsch',
  },
];

const glasPakete: Paket[] = [
  {
    name: 'Stufe 1 – Basis',
    for: 'Für die schnelle, streifenfreie Sichtreinigung der Glasflächen.',
    recommended: false,
    features: [
      'Glasflächen innen und außen reinigen',
      'Reinwassertechnik ohne Chemikalien für streifenfreies Trocknen',
      'Endkontrolle auf Streifenfreiheit',
    ],
  },
  {
    name: 'Stufe 2 – Komfort',
    for: 'Empfohlen, wenn das ganze Fenster gepflegt aussehen soll — nicht nur die Scheibe.',
    recommended: true,
    features: [
      'Alle Leistungen aus Stufe 1',
      'Rahmen und Falze reinigen',
      'Fensterbänke reinigen',
      'Glastüren, Glaswände und Trennwände reinigen',
    ],
  },
  {
    name: 'Stufe 3 – Premium',
    for: 'Für hohe, schwer erreichbare Fenster und hartnäckige Verschmutzungen.',
    recommended: false,
    features: [
      'Alle Leistungen aus Stufe 2',
      'Schwer erreichbare und hohe Fenster mit Teleskopstange oder Hebebühne',
      'Hartnäckige Verschmutzungen entfernen (Kalk, Vogelkot, Baustaub)',
      'Schaufenster und Ladenfronten reinigen',
    ],
  },
];

// Glas: der Schmerz ist nie die Scheibe an sich, sondern das halbe Ergebnis.
// Wo eine Aufloesung erst ab einer Stufe gilt, benennt proof die Stufe.
const glasPains: PainPoint[] = [
  {
    pain: 'Nach der Reinigung sieht man bei Sonnenlicht jeden Streifen.',
    consequence:
      'Das Fenster ist sauber und wirkt trotzdem ungepflegt — sichtbar für jeden, der hereinkommt.',
    solution:
      'Wir arbeiten mit Reinwassertechnik: entmineralisiertes Wasser trocknet rückstandsfrei, ganz ohne Chemikalien. Zum Schluss kontrollieren wir jedes Fenster auf Streifenfreiheit.',
    proof: 'Endkontrolle auf Streifenfreiheit',
  },
  {
    pain: 'Die Scheibe ist sauber, Rahmen und Falze sind es nicht.',
    consequence:
      'Der Schmutz aus den Falzen läuft beim nächsten Regen über die frisch geputzte Scheibe.',
    solution:
      'Ab Stufe 2 Komfort reinigen wir Rahmen, Falze und Fensterbänke mit — damit das Ergebnis länger hält als bis zum nächsten Wetterumschwung.',
    proof: 'Rahmen, Falze und Fensterbänke ab Stufe 2 Komfort',
  },
  {
    pain: 'An die oberen Fenster kommt seit Jahren niemand heran.',
    consequence:
      'Von außen wirkt das ganze Gebäude ungepflegt, obwohl im Erdgeschoss alles blitzt.',
    solution:
      'Mit Teleskopstange und bei Bedarf mit Hebebühne reinigen wir auch hohe und schwer erreichbare Fenster — ab Stufe 3 Premium.',
    proof: 'Hohe und schwer erreichbare Fenster ab Stufe 3 Premium',
  },
  {
    pain: 'Sie denken erst an die Fenster, wenn sie längst auffallen.',
    consequence:
      'Dann ist der Aufwand größer — und zwischendurch war der Eindruck monatelang schlecht.',
    solution:
      'Wir reinigen einmalig oder in festen Intervallen. Im Intervall steht der Termin fest, ohne dass Sie ihn jedes Mal neu vereinbaren müssen.',
    proof: 'Einmalig oder in festen Intervallen',
  },
];

// Fitnessstudio: der Betreiber verkauft ein Gefuehl. Hygiene entscheidet
// ueber Probetraining, Verlaengerung und Bewertung.
const fitnessPains: PainPoint[] = [
  {
    pain: 'Es riecht nach Schweiß, sobald jemand die Tür aufmacht.',
    consequence:
      'Probetrainierende entscheiden in den ersten dreißig Sekunden, ob sie wiederkommen. Der Geruch entscheidet mit.',
    solution:
      'Wir reinigen Trainingsflächen, Umkleiden und Sanitärbereiche in festen Intervallen und desinfizieren Geräte und Kontaktflächen nach Absprache.',
    proof: 'Hygienische Reinigung der Trainingsflächen',
  },
  {
    pain: 'Die Duschen sind der häufigste Beschwerdepunkt Ihrer Mitglieder.',
    consequence:
      'In Bewertungsportalen steht das dann dauerhaft — und kostet Sie Neuanmeldungen, die Sie nie zu Gesicht bekommen.',
    solution:
      'Duschen und Sanitärbereiche reinigen und desinfizieren wir bei jedem Termin, inklusive Auffüllen von Seife und Papiertüchern.',
    proof: 'Duschen und Sanitär inklusive Desinfektion',
  },
  {
    pain: 'Putzen im laufenden Betrieb stört — aber Ihr Studio hat kaum Schließzeiten.',
    consequence:
      'Entweder stehen Wischmopp und Trainierende sich im Weg, oder es wird nur oberflächlich gemacht.',
    solution:
      'Auf Wunsch reinigen wir außerhalb der Öffnungszeiten, auch früh morgens oder spät abends, wenn Ihr Studio lange geöffnet hat.',
    proof: 'Reinigung außerhalb der Öffnungszeiten möglich',
  },
  {
    pain: 'An die Geräte traut sich niemand, aus Angst etwas zu beschädigen.',
    consequence:
      'Griffe, Polster und Displays bleiben ausgespart — ausgerechnet die Flächen, die jeder anfasst.',
    solution:
      'Trainingsgeräte reinigen und desinfizieren wir nach Absprache, mit den Mitteln und Verfahren, die Sie für Ihre Geräte vorgeben.',
    proof: 'Trainingsgeräte nach Absprache',
  },
];

// Kitas und Schulen: die Leitung rechtfertigt sich vor Eltern und Traeger.
// Hygiene ist hier ein Kommunikationsthema, nicht nur ein Reinigungsthema.
const bildungPains: PainPoint[] = [
  {
    pain: 'Eine Infektionswelle zieht durch Gruppe oder Klasse und dann durch die Elternschaft.',
    consequence:
      'Erkrankte Kinder, ausfallende Betreuung oder Unterricht und Eltern, die wissen wollen, was Sie dagegen unternehmen.',
    solution:
      'Kontaktflächen reinigen wir konsequent bei jedem Termin. Die Desinfektion erfolgt nach Ihrer Vorgabe, nicht nach unserem Standard.',
    proof: 'Desinfektion nach Ihrer Vorgabe',
  },
  {
    pain: 'Gereinigt wird, während die Kinder noch im Haus sind.',
    consequence:
      'Reinigungsmittel und Kleinkinder im selben Raum sind ein Thema, das Sie Eltern nicht erklären möchten.',
    solution:
      'Auf Wunsch reinigen wir außerhalb der Betreuungs- und Unterrichtszeiten — morgens vor der Öffnung oder nach Schließung.',
    proof: 'Reinigung außerhalb der Betreuungs- und Unterrichtszeiten möglich',
  },
  {
    pain: 'Kinderbereiche werden gereinigt wie Erwachsenenräume.',
    consequence:
      'Niedrige Waschbecken, Spielflächen und Garderoben fallen durchs Raster, weil sie auf keiner Standardliste stehen.',
    solution:
      'Wir reinigen Gruppenräume, Spielflächen und Sanitärbereiche kindgerecht — einschließlich Tischen, Stühlen, Garderoben und Fluren.',
    proof: 'Hygienische Reinigung kindgerechter Räume',
  },
  {
    pain: 'In den Ferien wäre Zeit für die gründliche Reinigung — nur macht sie niemand.',
    consequence:
      'Über das Jahr sammelt sich an, was im laufenden Betrieb nicht geht, und wird nie aufgeholt.',
    solution:
      'Auf Wunsch übernehmen wir in den Schließzeiten eine Ferien- oder Intensivreinigung, zusätzlich zur regelmäßigen Unterhaltsreinigung.',
    proof: 'Ferien- und Intensivreinigung auf Wunsch',
  },
];

const bauschlussPains: PainPoint[] = [
  {
    pain: 'Der Übergabetermin steht, die Handwerker sind zwei Tage im Verzug.',
    consequence:
      'Die Reinigung ist das letzte Glied in der Kette — und bekommt die Verspätung aller anderen Gewerke ab.',
    solution:
      'Wir sind kurzfristig einsatzbereit und richten uns nach Ihrem Übergabetermin, nicht nach unserem Kalender.',
    proof: 'Schnelle Einsatzbereitschaft für pünktliche Übergabe',
  },
  {
    pain: 'Zementschleier und Farbspritzer gehen mit normalem Wischen nicht ab.',
    consequence:
      'Beim Abnahmetermin fällt genau das auf — und landet in der Mängelliste.',
    solution:
      'Zementschleier, Kalkflecken und Farbspritzer entfernen wir gezielt mit den dafür geeigneten Mitteln, nicht durch mehrfaches Nachwischen.',
    proof: 'Entfernung von Zementschleier, Kalkflecken und Farbspritzern',
  },
  {
    pain: 'Zwei Tage nach der Reinigung liegt der Feinstaub wieder überall.',
    consequence:
      'Baustaub setzt sich in Lüftungsgittern, Heizkörpern und Fußleisten ab und rieselt nach, sobald jemand die Tür öffnet.',
    solution:
      'Wir nehmen Feinstaub mit professionellen Industriesaugern auf und reinigen auch Heizkörper, Lüftungsgitter und Fußleisten — genau dort, wo er sonst liegen bleibt.',
    proof: 'Feinstaubaufnahme mit Industriesaugern',
  },
  {
    pain: 'Bereits gereinigte Räume werden beim Weiterarbeiten wieder schmutzig.',
    consequence:
      'Am Ende putzt jemand dieselbe Fläche dreimal — und beim Rundgang ist trotzdem etwas übersehen.',
    solution:
      'Wir arbeiten systematisch von oben nach unten und von innen nach außen, mit Abschlusskontrolle vor der Übergabe.',
    proof: 'Abschlusskontrolle vor der bezugsfertigen Übergabe',
  },
];

const wohnungPains: PainPoint[] = [
  {
    pain: 'Zwischen Beruf und Familie bleibt der Haushalt einfach liegen.',
    consequence:
      'Das schlechte Gewissen kommt am Wochenende dazu — genau dann, wenn eigentlich Zeit für anderes wäre.',
    solution:
      'Wir übernehmen die Reinigung regelmäßig oder einmalig, in dem Umfang, den Sie festlegen. Sie bestimmen, welche Räume gemacht werden.',
    proof: 'Regelmäßig oder einmalig buchbar',
  },
  {
    pain: 'Eine fremde Person in der eigenen Wohnung ist Vertrauenssache.',
    consequence:
      'Bei wechselnden Kräften fängt dieses Vertrauen jedes Mal wieder bei null an.',
    solution:
      'Auf Wunsch kommt immer dieselbe vertraute Reinigungskraft, die Ihre Wohnung und Ihre Vorlieben kennt.',
    proof: 'Feste, vertraute Reinigungskraft auf Wunsch',
  },
  {
    pain: 'Sie müssten zu Hause sein, wenn gereinigt wird.',
    consequence:
      'Ein Termin, der zu Ihrem Arbeitstag passt, findet sich selten — und dann verschiebt sich wieder alles.',
    solution:
      'Auf Wunsch reinigen wir in Ihrer Abwesenheit, mit Schlüsselservice. Reinigungsmittel und Geräte bringen wir mit.',
    proof: 'Schlüsselservice auf Wunsch',
  },
  {
    pain: 'Bei der Wohnungsübergabe hängt die Kaution an der Sauberkeit.',
    consequence:
      'Der Vermieter findet die Stellen, die man nach Jahren in der eigenen Wohnung selbst nicht mehr sieht.',
    solution:
      'Für Ein- und Auszug übernehmen wir die Übergabereinigung, auf Wunsch einschließlich Fenster, Kühlschrank und Backofen.',
    proof: 'Übergabereinigung bei Ein- oder Auszug',
  },
];

const grundPains: PainPoint[] = [
  {
    pain: 'Es ist geputzt und sieht trotzdem nicht sauber aus.',
    consequence:
      'Fugen, Ecken und Ablagerungen bekommt die Unterhaltsreinigung nicht mehr weg — der Eindruck bleibt stumpf.',
    solution:
      'Die Grundreinigung geht bis in Fugen und Ecken und entfernt auch Schmutz, der sich über längere Zeit angesammelt hat.',
    proof: 'Intensive Tiefenreinigung bis in Fugen und Ecken',
  },
  {
    pain: 'Kalk und Fett gehen mit normalen Mitteln nicht mehr ab.',
    consequence:
      'Man gewöhnt sich daran — oder tauscht irgendwann Armaturen und Fronten aus, die eigentlich noch gut sind.',
    solution:
      'Wir entkalken und entfetten hartnäckige Ablagerungen gezielt: Herd, Dunstabzug, Fliesen, Fugen und Abflüsse.',
    proof: 'Entkalkung und Entfettung hartnäckiger Ablagerungen',
  },
  {
    pain: 'Vor der Neuvermietung soll das Objekt wieder wie neu wirken.',
    consequence:
      'Zwischen Auszug und Besichtigung bleiben oft nur wenige Tage, und der Zustand entscheidet mit über die erzielbare Miete.',
    solution:
      'Wir bringen Flächen wieder auf ein hohes Ausgangsniveau — auf Wunsch einschließlich Bodenaufbereitung und Neubeschichtung.',
    proof: 'Bodenaufbereitung und Neubeschichtung auf Wunsch',
  },
  {
    pain: 'Teppiche und Polster riechen, obwohl regelmäßig abgesaugt wird.',
    consequence:
      'Absaugen holt den Oberflächenschmutz, nicht das, was tief in den Fasern sitzt.',
    solution:
      'Teppiche und Polster reinigen wir mit Sprühextraktion — das löst den Schmutz aus der Faser, statt ihn nur abzutragen.',
    proof: 'Teppiche und Polster mit Sprühextraktion',
  },
];

const sonderPains: PainPoint[] = [
  {
    pain: 'Nach der Firmenfeier sieht das Büro aus — und um acht kommen die ersten Mitarbeiter.',
    consequence:
      'Niemand im Team hat Zeit dafür, und am Ende bleibt es an einer einzigen Person hängen.',
    solution:
      'Wir übernehmen die Reinigung nach Firmenfeiern, Schulungen und Veranstaltungen, auch kurzfristig und außerhalb des regulären Reinigungsplans.',
    proof: 'Reinigung nach Veranstaltungen',
  },
  {
    pain: 'Die Gewerbefläche muss übergeben werden, und der Zustand ist vertraglich geregelt.',
    consequence:
      'Was Vermieter oder Nachmieter beanstanden, kostet Sie am Ende Geld oder eine unangenehme Nachverhandlung.',
    solution:
      'Wir übernehmen die Umzugs- und Übergabereinigung für Büros, Praxen und Gewerbeflächen, abgestimmt auf den vereinbarten Übergabezustand.',
    proof: 'Umzugs- und Übergabereinigung für Gewerbeflächen',
  },
  {
    pain: 'Etwas ist passiert, und es muss heute noch sauber sein.',
    consequence:
      'Der reguläre Reinigungstag ist erst Donnerstag. Der Zwischenfall richtet sich nicht danach.',
    solution:
      'Zusatz- und Akutreinigung außerhalb des regulären Plans übernehmen wir auch einmalig und kurzfristig.',
    proof: 'Zusatz- und Akutreinigung außerhalb des regulären Plans',
  },
  {
    pain: 'Für den einen Sonderfall lohnt sich keine ganze Anbietersuche.',
    consequence:
      'Drei Angebote einholen, vergleichen, Termine abstimmen — für eine Reinigung, die genau einmal stattfindet.',
    solution:
      'Nach kurzer Besichtigung oder Abstimmung erhalten Sie ein transparentes Angebot passend zum Umfang. Auch für einzelne Bereiche statt ganzer Objekte.',
    proof: 'Angebot nach kurzer Abstimmung',
  },
];

export const services: Service[] = [
  {
    slug: 'praxisreinigung',
    icon: Stethoscope,
    title: 'Praxisreinigung',
    shortDesc: 'Spezialisierte Reinigung für Arzt-, Zahnarzt- und Therapiepraxen in drei aufeinander aufbauenden Paketen.',
    price: 'ab 33,50 €/Std. netto zzgl. MwSt.',
    items: ['Praxis Reinigung', 'Praxis Plus', 'Praxis Komfort'],
    image: 'praxisreinigung',
    showcaseImage: 'behandlungszimmer',
    heroTitle: 'Spezialisierte Praxisreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Zuverlässige Reinigung für Arztpraxen, Zahnarztpraxen, Therapiepraxen und medizinische Einrichtungen — nach Praxisschluss, mit festen Ansprechpartnern und klaren Abläufen.',
    intro:
      'In Arzt-, Zahnarzt- und Therapiepraxen gelten besondere Anforderungen an Sauberkeit und Hygiene — Patienten und Mitarbeiter erwarten zu Recht höchste Standards. Wir reinigen nach Praxisschluss, außerhalb der Sprechzeiten, nach dem Hygieneplan Ihrer Praxis und trennen Behandlungs-, Sanitär- und Wartebereiche strikt. Ab dem Paket Praxis Plus arbeiten wir zusätzlich nach einem individuellen Reinigungs- und Desinfektionsplan, mit speziell eingewiesenem Personal und farbcodierten Materialien. Wählen Sie aus drei aufeinander aufbauenden Paketen die passende Lösung für Ihre Praxis.',
    // Deckungsgleich mit `praxisPakete`: Was erst ab einer Stufe gilt, nennt die Stufe.
    benefits: [
      'Reinigung nach Praxisschluss an festen Reinigungstagen',
      'Vertretungsregelung bei Ausfall in allen drei Paketen',
      'Ab Praxis Plus: individueller Reinigungs- und Desinfektionsplan',
      'Ab Praxis Plus: speziell eingewiesenes Personal und farbcodierte Materialien',
      'Ab Praxis Plus: Reinigungsprotokolle auf Wunsch für Ihre QM-Dokumentation',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Empfang und Wartebereich reinigen',
      'Behandlungs- und Sprechzimmer nach Praxisvorgabe reinigen',
      'Kontaktflächen nach Hygieneplan desinfizieren',
      'Sanitärbereiche reinigen inkl. Flächendesinfektion (VAH-gelistete Mittel)',
      'Getrennte Reinigung von Behandlungs-, Sanitär- und Wartezonen',
      'Böden saugen und wischen',
      'Papierkörbe leeren und Abfall entsorgen',
      'Verbrauchsmaterial auffüllen (Seife, Papiertücher)',
      'Nebenräume und Küche reinigen',
    ],
    pakete: praxisPakete,
    paketeTitle: 'Drei einfache Praxis-Pakete',
    paketeIntro:
      'Wählen Sie die passende Lösung für Ihre Praxis. Der genaue Festpreis wird nach einer kostenlosen Besichtigung erstellt und richtet sich nach Praxisgröße, Reinigungsintervall und Leistungsumfang.',
    paketeHint: 'Festpreis nach kostenloser Besichtigung. Reinigung und Flächendesinfektion erfolgen nach Absprache und passend zum Hygieneplan Ihrer Praxis.',
    painHeadline: 'Vier Probleme, die wir in Praxen immer wieder hören',
    pains: praxisPains,
    faqHeadline: 'Häufige Fragen zur Praxisreinigung',
    faqs: praxisFaqs,
    seo: {
      title: 'Praxisreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Spezialisierte Praxisreinigung für Arzt-, Zahnarzt- und Therapiepraxen in Bielefeld, Gütersloh und Umgebung. Reinigung nach Praxisschluss, feste Ansprechpartner, transparente Festpreise.',
      keywords: 'Praxisreinigung, Arztpraxis Reinigung, Zahnarztpraxis Reinigung, Therapiepraxis, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'bueroreinigung',
    icon: Briefcase,
    title: 'Büroreinigung',
    shortDesc: 'Saubere Büros für ein angenehmes und professionelles Arbeitsumfeld.',
    price: 'ab 31,50 €/Std. netto zzgl. MwSt.',
    items: ['Regelmäßige Büroreinigung', 'Büroreinigung nach Nutzung', 'Büro-Intensivreinigung'],
    image: 'bueroreinigung',
    showcaseImage: 'bueroreinigung-showcase',
    heroTitle: 'Büroreinigung in Gütersloh, Bielefeld und Umgebung',
    heroText:
      'Ein sauberes Büro sorgt für ein angenehmes Arbeitsklima und einen professionellen Eindruck bei Kunden und Mitarbeitern. Wir reinigen zuverlässig und flexibel nach Ihrem Bedarf.',
    intro:
      'Ein sauberes Arbeitsumfeld steigert das Wohlbefinden und die Produktivität Ihrer Mitarbeiter und hinterlässt bei Kunden einen professionellen Eindruck. Wir reinigen zu Ihren Wunschzeiten — vor Arbeitsbeginn, nach Feierabend oder am Wochenende — mit einem festen, eingewiesenen Team und nach dokumentierten Checklisten. Regelmäßige Qualitätskontrollen sorgen dafür, dass unser Standard dauerhaft eingehalten wird.',
    benefits: [
      'Flexible Reinigungszeiten (vor, nach oder außerhalb der Arbeitszeit)',
      'Festes, eingewiesenes Reinigungsteam',
      'Dokumentierte Checklisten je Raum',
      'Regelmäßige Qualitätskontrollen',
      'Desinfektionsreinigung von Kontaktflächen auf Wunsch',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Freie Schreibtischflächen abwischen',
      'Böden saugen und wischen',
      'Papierkörbe leeren und Abfall entsorgen',
      'Kontaktflächen abwischen (Türklinken, Lichtschalter, Telefone)',
      'Sanitär- und WC-Bereiche reinigen und auffüllen',
      'Küche und Teeküche reinigen',
      'Glastüren und Trennwände von Flecken befreien',
      'Empfangs- und Besprechungsräume reinigen',
      'Monitore und Tastaturen nach Absprache reinigen',
    ],
    painHeadline: 'Vier Probleme, die wir in Büros immer wieder hören',
    pains: bueroPains,
    local: {
      headline: 'Büroreinigung in Gütersloh — direkt vom Firmensitz aus',
      paragraphs: [
        'Unser Firmensitz liegt in Gütersloh. Büros in der Stadt und in den umliegenden Orten erreichen wir auf kurzen Wegen — für die regelmäßige Reinigung genauso wie für die Besichtigung vorab.',
        'Zur kostenlosen Besichtigung kommt Ihre Objektleitung persönlich zu Ihnen. Sie bleibt danach Ihr fester Ansprechpartner und kommt selbst vorbei, wenn einmal etwas nicht passt.',
      ],
      orteTitle: 'Rund um Gütersloh im Einsatz',
      orte: [
        'Gütersloh',
        'Rheda-Wiedenbrück',
        'Verl',
        'Harsewinkel',
        'Herzebrock-Clarholz',
        'Rietberg',
        'Steinhagen',
        'Halle (Westf.)',
        'Schloß Holte-Stukenbrock',
      ],
    },
    seo: {
      title: 'Büroreinigung Gütersloh & Bielefeld | AP Clean Services',
      description:
        'Büroreinigung in Gütersloh, Bielefeld und Umgebung — vom Firmensitz in Gütersloh aus, mit persönlicher Objektleitung. Regelmäßig, nach Nutzung oder als Intensivreinigung.',
      keywords: 'Büroreinigung Gütersloh, Büroreinigung Bielefeld, Unterhaltsreinigung, Gewerbereinigung',
    },
  },
  {
    slug: 'treppenhausreinigung',
    icon: FloorIcon,
    title: 'Treppenhausreinigung',
    shortDesc: 'Gepflegte Treppenhäuser für Mehrfamilienhäuser und Hausverwaltungen.',
    price: 'ab 31,50 €/Std. netto zzgl. MwSt.',
    items: ['Für Mehrfamilienhäuser', 'Für Hausverwaltungen', 'Treppenhaus-Intensivreinigung'],
    image: 'treppenhausreinigung',
    showcaseImage: 'treppenhausreinigung-showcase',
    heroTitle: 'Treppenhausreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Ein gepflegtes Treppenhaus ist die Visitenkarte jedes Gebäudes. Wir sorgen für dauerhaft saubere Gemeinschaftsflächen — zuverlässig und in festen Intervallen.',
    intro:
      'Das Treppenhaus ist die Visitenkarte jedes Mehrfamilienhauses: Ein gepflegter Eingangsbereich schafft Vertrauen, steigert die Zufriedenheit der Mieter und erhält den Wert der Immobilie. Wir reinigen systematisch von oben nach unten — Stufen, Absätze, Handläufe, Eingangsbereich und Briefkastenanlagen — mit festem Mitarbeiter und organisierter Vertretung bei Urlaub oder Krankheit. Die Abrechnung bereiten wir so auf, dass sie direkt auf die Nebenkosten umgelegt werden kann.',
    benefits: [
      'Fester Mitarbeiter mit organisierter Vertretung',
      'Wöchentliche oder 14-tägige Reinigung',
      'Systematische Reinigung von oben nach unten',
      'Abrechnung für die Nebenkostenumlage aufbereitet',
      'Erfahrung mit Hausverwaltungen',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Treppenstufen und Absätze wischen',
      'Handläufe und Geländer abwischen (Desinfektion möglich)',
      'Eingangstüren und Glasflächen im Eingangsbereich reinigen',
      'Briefkastenanlagen und Klingeltableaus abwischen',
      'Fußmatten und Fußabstreifer reinigen',
      'Lampen und Lichtschalter entstauben',
      'Kellergänge, Fahrrad- und Müllräume auf Wunsch',
      'Treppenhausfenster in größeren Intervallen auf Wunsch',
      'Meldung von Auffälligkeiten',
    ],
    painHeadline: 'Vier Probleme, die Hausverwaltungen mit der Treppenhausreinigung kennen',
    pains: treppenhausPains,
    seo: {
      title: 'Treppenhausreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Treppenhausreinigung für Mehrfamilienhäuser und Hausverwaltungen in Bielefeld, Gütersloh und Umgebung. Zuverlässig, in festen Intervallen.',
      keywords: 'Treppenhausreinigung, Hausverwaltung, Mehrfamilienhaus, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'glasreinigung',
    icon: Sparkles,
    title: 'Glas- und Fensterreinigung',
    shortDesc: 'Streifenfreie Fenster und Glasflächen – privat und gewerblich.',
    price: 'ab 41,00 €/Std. netto zzgl. MwSt.',
    items: ['Privat & Kleinobjekte', 'Gewerbliche Glasreinigung', 'Glas-Sonderreinigung'],
    image: 'glasreinigung',
    showcaseImage: 'glasreinigung-showcase',
    heroTitle: 'Glas- und Fensterreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Streifenfreie Fenster und klare Glasflächen sorgen für mehr Licht und einen gepflegten Eindruck — für Privathaushalte ebenso wie für Gewerbeobjekte.',
    intro:
      'Saubere Fenster lassen mehr Tageslicht herein, verbessern das Erscheinungsbild Ihres Gebäudes und schaffen ein angenehmes Raumklima. Wir arbeiten mit der Reinwassertechnik mit entmineralisiertem Wasser für streifenfreie Ergebnisse ohne Chemikalien und reinigen Glasflächen, Rahmen und Falze — auf Wunsch inklusive Fensterbänke. Für schwer erreichbare Fenster setzen wir Teleskopstangen und bei Bedarf Hebebühnen ein; abschließend kontrollieren wir jedes Fenster auf Streifenfreiheit.',
    benefits: [
      'Streifenfreie Ergebnisse mit Reinwassertechnik',
      'Rahmen, Falze und Fensterbänke ab Stufe 2 Komfort',
      'Hohe und schwer erreichbare Fenster ab Stufe 3 Premium',
      'Privat, Gewerbe, Einzelhandel und Industrie',
      'Einmalig oder in festen Intervallen',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Glasflächen innen und außen reinigen',
      'Rahmen und Falze reinigen',
      'Fensterbänke auf Wunsch reinigen',
      'Glastüren, Schaufenster und Ladenfronten reinigen',
      'Glaswände und Trennwände reinigen',
      'Reinwassertechnik ohne Chemikalien für streifenfreies Trocknen',
      'Hartnäckige Verschmutzungen entfernen (Kalk, Vogelkot, Baustaub)',
      'Schwer erreichbare Fenster mit Teleskopstange oder Hebebühne',
      'Endkontrolle auf Streifenfreiheit',
    ],
    pakete: glasPakete,
    paketeTitle: 'Drei Glasreinigungs-Stufen',
    paketeIntro:
      'Wählen Sie den passenden Umfang für Ihre Fenster und Glasflächen. Der genaue Festpreis wird nach einer kostenlosen Besichtigung erstellt und richtet sich nach Anzahl, Größe und Zustand der Flächen.',
    paketeHint: 'Festpreis nach kostenloser Besichtigung. Umfang und Intervall stimmen wir individuell auf Ihre Glasflächen ab.',
    painHeadline: 'Vier Probleme, die wir bei Fenstern immer wieder sehen',
    pains: glasPains,
    seo: {
      title: 'Glas- & Fensterreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Streifenfreie Glas- und Fensterreinigung in Bielefeld, Gütersloh und Umgebung. Privat und gewerblich, einmalig oder regelmäßig.',
      keywords: 'Glasreinigung, Fensterreinigung, Schaufenster, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'fitnessstudio-reinigung',
    icon: Dumbbell,
    title: 'Fitnessstudio-Reinigung',
    shortDesc: 'Hygienische Reinigung von Trainingsflächen, Umkleiden und Sanitärbereichen.',
    price: 'ab 32,50 €/Std. netto zzgl. MwSt.',
    items: ['Trainingsflächen-Reinigung', 'Umkleide- und Sanitärreinigung', 'Studio-Komplettbetreuung'],
    image: 'fitnessstudio-reinigung',
    showcaseImage: 'fitnessstudio-reinigung-showcase',
    heroTitle: 'Fitnessstudio-Reinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Hygiene ist im Fitnessstudio entscheidend. Wir sorgen für saubere Trainingsflächen, gepflegte Umkleiden und einwandfreie Sanitärbereiche.',
    intro:
      'Von der Reinigung der Trainingsflächen über Umkleiden und Sanitärbereiche bis zur kompletten Studio-Betreuung — wir halten Ihr Studio hygienisch sauber und einladend.',
    benefits: [
      'Hygienische Reinigung der Trainingsflächen',
      'Umkleiden und Sanitärbereiche',
      'Reinigung außerhalb der Öffnungszeiten möglich',
      'Feste Intervalle nach Bedarf',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Trainingsgeräte nach Absprache reinigen und desinfizieren',
      'Trainingsböden reinigen',
      'Umkleiden und Spinde abwischen',
      'Duschen und Sanitärbereiche reinigen und desinfizieren',
      'Spiegel und Glasflächen reinigen',
      'Papierkörbe leeren und Abfall entsorgen',
      'Verbrauchsmaterial auffüllen (Seife, Papiertücher)',
      'Empfang und Aufenthaltsbereich reinigen',
      'Kontaktflächen abwischen',
      'Matten und Kursräume nach Absprache reinigen',
    ],
    painHeadline: 'Vier Probleme, die Studiobetreiber kennen',
    pains: fitnessPains,
    seo: {
      title: 'Fitnessstudio-Reinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Hygienische Fitnessstudio-Reinigung in Bielefeld, Gütersloh und Umgebung. Trainingsflächen, Umkleiden und Sanitärbereiche — zuverlässig und diskret.',
      keywords: 'Fitnessstudio Reinigung, Studioreinigung, Sanitärreinigung, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'bildungseinrichtungen-reinigung',
    icon: GraduationCap,
    title: 'Kita- & Schulreinigung',
    shortDesc: 'Saubere Räume für Kitas, Kindergärten, Schulen sowie Betreuungs- und Schulungsräume.',
    price: 'ab 31,50 €/Std. netto zzgl. MwSt.',
    items: ['Private Kitas und Betreuungsräume', 'Private und öffentliche Schulen', 'Nachhilfe- und Schulungsräume'],
    image: 'bildungseinrichtungen-reinigung',
    showcaseImage: 'bildungseinrichtungen-reinigung-showcase',
    heroTitle: 'Kita- & Schulreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Saubere und hygienische Räume sind in Kitas, Kindergärten und Schulen besonders wichtig. Wir reinigen Gruppen-, Klassen-, Betreuungs- und Schulungsräume zuverlässig und gründlich.',
    intro:
      'Für private Kitas und Kindergärten, private und öffentliche Schulen, Betreuungsräume sowie Nachhilfe- und Schulungsräume bieten wir eine regelmäßige, hygienische Reinigung. Auf Wunsch auch als Ferien- oder Intensivreinigung.',
    benefits: [
      'Hygienische Reinigung kindgerechter Räume',
      'Reinigung außerhalb der Betreuungs- und Unterrichtszeiten möglich',
      'Klassenräume, Turnhallen und Mensen',
      'Nachhilfe- und Schulungsräume',
      'Ferien- und Intensivreinigung auf Wunsch',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Gruppen-, Klassen-, Fach- und Schulungsräume reinigen',
      'Tafeln und Whiteboards reinigen',
      'Tische, Stühle und Spielflächen abwischen',
      'Kontaktflächen reinigen, Desinfektion nach Vorgabe',
      'Böden saugen und wischen',
      'Sanitärbereiche kindgerecht reinigen',
      'Papierkörbe leeren und Abfall entsorgen',
      'Verbrauchsmaterial auffüllen (Seife, Papiertücher)',
      'Garderoben und Flure reinigen',
      'Lehrerzimmer, Sekretariat und Verwaltung reinigen',
      'Turnhallen, Umkleiden und Duschen reinigen',
      'Küchen-, Ess- und Mensabereiche sowie Aufenthaltsräume nach Vereinbarung reinigen',
    ],
    painHeadline: 'Vier Probleme, die Kitas und Schulen kennen',
    pains: bildungPains,
    seo: {
      title: 'Kita- & Schulreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Kita- & Schulreinigung in Bielefeld, Gütersloh und Umgebung: Kitas, Kindergärten, Schulen und Schulungsräume. Hygienisch und außerhalb Ihrer Öffnungszeiten möglich.',
      keywords: 'Kita Reinigung, Kindergartenreinigung, Schulreinigung, Reinigung Bildungseinrichtungen, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'sonderreinigung',
    icon: Wrench,
    title: 'Sonder- & Anlassreinigung',
    shortDesc: 'Flexible Reinigung nach besonderen Anlässen und kurzfristigem Bedarf.',
    price: 'ab 36,50 €/Std. netto zzgl. MwSt.',
    items: ['Umzugs- und Übergabereinigung', 'Reinigung nach Veranstaltungen', 'Zusatz- und Akutreinigung'],
    image: 'sonderreinigung',
    showcaseImage: 'toilette',
    heroTitle: 'Sonder- & Anlassreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Nicht jede Reinigung ist regelmäßig planbar. Deshalb unterstützen wir Sie auch bei einmaligen oder kurzfristigen Reinigungsanlässen — zum Beispiel nach einem Umzug, vor einer Objektübergabe, nach einer Veranstaltung oder bei zusätzlichem Reinigungsbedarf.',
    intro:
      'Nach kurzer Besichtigung oder Abstimmung erhalten Sie ein transparentes Angebot passend zum Umfang der gewünschten Reinigung.',
    benefits: [
      'Umzugs- und Übergabereinigung für Büros, Praxen und Gewerbeflächen',
      'Reinigung nach Firmenfeiern, Schulungen und Veranstaltungen',
      'Zusatz- und Akutreinigung außerhalb des regulären Plans',
      'Flexibel nach Anlass und Bedarf',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Umzugs- und Übergabereinigung',
      'Reinigung nach Veranstaltungen',
      'Zusatz- und Akutreinigung',
      'Reinigung einzelner Bereiche',
      'Sanitär- und Küchenreinigung nach Nutzung',
      'Böden saugen und wischen',
      'Müllentsorgung nach Vereinbarung',
      'Glasreinigung optional',
    ],
    painHeadline: 'Vier Situationen, für die es keinen Reinigungsplan gibt',
    pains: sonderPains,
    seo: {
      title: 'Sonder- & Anlassreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Flexible Sonder- & Anlassreinigung in Bielefeld, Gütersloh und Umgebung — nach Umzug, nach Veranstaltungen oder bei kurzfristigem Bedarf.',
      keywords: 'Sonderreinigung, Anlassreinigung, Umzugsreinigung, Veranstaltungsreinigung, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'bauschlussreinigung',
    icon: HardHat,
    title: 'Bauschlussreinigung',
    shortDesc: 'Bezugsfertig sauber nach Neubau, Umbau oder Renovierung.',
    price: 'ab 35,50 €/Std. netto zzgl. MwSt.',
    items: ['Bauendreinigung nach Neubau', 'Reinigung nach Umbau & Renovierung', 'Grob- und Feinreinigung'],
    image: 'bauschlussreinigung',
    heroTitle: 'Bauschlussreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Nach Neubau, Umbau oder Renovierung übernehmen wir die abschließende Reinigung, damit Ihre Räume bezugsfertig und makellos sauber übergeben werden können.',
    intro:
      'Nach Bau- und Renovierungsarbeiten bleiben große Mengen an Baustaub, Schmutz und Materialresten zurück. Bevor Mieter einziehen oder Eigentümer ihr Objekt beziehen können, muss alles gründlich gereinigt werden. Wir arbeiten systematisch von oben nach unten und von innen nach außen — so wird kein Bereich vergessen und bereits gereinigte Flächen nicht erneut verschmutzt. Vom groben Baustaub bis zur streifenfreien Feinreinigung machen wir Ihr Objekt zuverlässig bezugsfertig.',
    benefits: [
      'Systematische Reinigung von oben nach unten',
      'Entfernung von Zementschleier, Kalkflecken und Farbspritzern',
      'Feinstaubaufnahme mit professionellen Industriesaugern',
      'Fenster, Böden, Sanitär und Oberflächen',
      'Schnelle Einsatzbereitschaft für pünktliche Übergabe',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Baustaub von Oberflächen, Decken und Wänden entfernen',
      'Alle Böden reinigen (Fliesen, Parkett, Laminat, Estrich)',
      'Fenster innen und außen inkl. Rahmen und Falze reinigen',
      'Zementschleier, Kalkflecken und Farbspritzer entfernen',
      'Sanitäranlagen reinigen (WC, Waschbecken, Dusche, Badewanne)',
      'Küche inklusive Einbaugeräte reinigen',
      'Türen, Türrahmen und Fußleisten reinigen',
      'Heizkörper und Lüftungsgitter reinigen',
      'Abschlusskontrolle vor der bezugsfertigen Übergabe',
    ],
    painHeadline: 'Vier Dinge, die kurz vor der Übergabe schiefgehen',
    pains: bauschlussPains,
    seo: {
      title: 'Bauschlussreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Bauschluss- und Bauendreinigung in Bielefeld, Gütersloh und Umgebung. Bezugsfertig sauber nach Neubau, Umbau oder Renovierung.',
      keywords: 'Bauschlussreinigung, Bauendreinigung, Baureinigung, Neubau, Renovierung, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'wohnungsreinigung',
    icon: Home,
    title: 'Wohnungsreinigung',
    shortDesc: 'Gründliche Reinigung für Ihr Zuhause – auf Wunsch bis zur Übergabe.',
    price: 'ab 31,50 €/Std. netto zzgl. MwSt.',
    items: ['Regelmäßige Wohnungsreinigung', 'Grundreinigung der Wohnung', 'Übergabe- und Umzugsreinigung'],
    image: 'wohnungsreinigung',
    heroTitle: 'Wohnungsreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Ob regelmäßig oder einmalig — wir reinigen Ihre Wohnung gründlich und zuverlässig, damit Sie sich in einem sauberen Zuhause wohlfühlen.',
    intro:
      'Im hektischen Alltag bleibt zwischen Beruf, Familie und Freizeit oft wenig Zeit für den Haushalt. Unsere Wohnungsreinigung schafft Ihnen Freiräume für die wirklich wichtigen Dinge — auf Wunsch kommt immer dieselbe vertraute Reinigungskraft, die Ihre Wohnung und Ihre Vorlieben kennt. Sie bestimmen, welche Räume gereinigt werden; Reinigungsmittel und Geräte bringen wir mit. Ob regelmäßig oder einmalig, auf Wunsch auch in Ihrer Abwesenheit.',
    benefits: [
      'Feste, vertraute Reinigungskraft auf Wunsch',
      'Individuell nach Ihren Wünschen',
      'Reinigungsmittel und Geräte werden gestellt',
      'Regelmäßig oder einmalig buchbar',
      'Schlüsselservice auf Wunsch',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Staubwischen und Staubsaugen aller Räume',
      'Hartböden wischen (Fliesen, Parkett, Laminat)',
      'Küche: Arbeitsflächen, Herd, Spüle und Fronten reinigen',
      'Kühlschrank und Backofen auf Wunsch reinigen',
      'Bad: WC, Waschbecken, Dusche/Badewanne, Spiegel und Armaturen',
      'Schlafzimmer: Betten machen und Staub wischen',
      'Wohnzimmer: Staub wischen und Polstermöbel absaugen',
      'Fenster auf Wunsch reinigen',
      'Übergabereinigung bei Ein- oder Auszug',
    ],
    painHeadline: 'Vier Gründe, warum die Wohnungsreinigung liegen bleibt',
    pains: wohnungPains,
    seo: {
      title: 'Wohnungsreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Gründliche Wohnungsreinigung in Bielefeld, Gütersloh und Umgebung. Regelmäßig, als Grundreinigung oder zur Wohnungsübergabe.',
      keywords: 'Wohnungsreinigung, Haushaltsreinigung, Umzugsreinigung, Übergabereinigung, Bielefeld, Gütersloh',
    },
  },
  {
    slug: 'grundreinigung',
    icon: SprayCan,
    title: 'Grundreinigung',
    shortDesc: 'Intensive Tiefenreinigung für stark beanspruchte und hartnäckige Flächen.',
    price: 'ab 10,90 €/m² netto zzgl. MwSt.',
    items: ['Intensive Tiefenreinigung', 'Böden, Sanitär & Küche', 'Ein- oder mehrmalig'],
    image: 'grundreinigung-tief',
    heroTitle: 'Grundreinigung in Bielefeld, Gütersloh und Umgebung',
    heroText:
      'Wenn die normale Unterhaltsreinigung nicht mehr ausreicht, sorgt die Grundreinigung für eine gründliche Tiefenreinigung — für hartnäckige Verschmutzungen und stark beanspruchte Flächen.',
    intro:
      'Manchmal braucht es mehr als die übliche Unterhaltsreinigung: Hartnäckige Verschmutzungen, jahrelange Ablagerungen oder besondere Anlässe erfordern eine intensive Tiefenreinigung. Wir gehen systematisch vor — zuerst Decken und Wände, dann Möbel und Oberflächen, zuletzt die Böden — und entfernen auch Schmutz, der sich über längere Zeit angesammelt hat. Ob als einmalige Frischekur, zum Frühjahrsputz oder vor einer Neuvermietung: Wir bringen Ihre Flächen wieder auf ein hohes Ausgangsniveau.',
    benefits: [
      'Intensive Tiefenreinigung bis in Fugen und Ecken',
      'Entkalkung und Entfettung hartnäckiger Ablagerungen',
      'Bodenaufbereitung und Neubeschichtung auf Wunsch',
      'Böden, Sanitär, Küche und Oberflächen',
      'Einmalig oder in festen Intervallen',
      'Kostenlose Objektbesichtigung',
    ],
    checklist: [
      'Küche entfetten: Herd, Dunstabzug, Schränke und Fliesen',
      'Bereiche hinter und unter Geräten reinigen',
      'Bad: Fugen tiefenreinigen, entkalken und Abflüsse reinigen',
      'Böden grundreinigen und Beschichtungen entfernen',
      'Fenster intensiv reinigen inkl. Rahmen, Falze und Rollläden',
      'Wände und Decken von Ablagerungen befreien',
      'Heizkörper auch zwischen den Lamellen reinigen',
      'Teppiche und Polster mit Sprühextraktion reinigen',
      'Abschlusskontrolle jeder gereinigten Fläche',
    ],
    painHeadline: 'Vier Anzeichen, dass die normale Reinigung nicht mehr reicht',
    pains: grundPains,
    seo: {
      title: 'Grundreinigung Bielefeld & Gütersloh | AP Clean Services',
      description:
        'Intensive Grundreinigung in Bielefeld, Gütersloh und Umgebung. Tiefenreinigung für hartnäckige Verschmutzungen und stark beanspruchte Flächen.',
      keywords: 'Grundreinigung, Tiefenreinigung, Intensivreinigung, Bielefeld, Gütersloh',
    },
  },
];

export const getServiceBySlug = (slug: string | undefined): Service | undefined =>
  services.find((s) => s.slug === slug);
